<template>
  <div id="app">
    <router-view />

    <div v-if="isMobile" class="mobile-tip" ref="mobileTip">
      <span class="tip-text">请在电脑端网页浏览器中使用 (推荐Chrome)</span>
      <button class="copy-button" @click="copyLink">复制链接</button>
    </div>

    <!-- AI聊天窗口 (在 LoadingPage 和 PaymentPage 不显示) -->
    <AIChatWindow v-if="shouldShowChat" />
  </div>
</template>

<script>
// 导入 vuex 辅助函数
import { mapState } from 'vuex'
// 导入 AI 聊天窗口组件
import AIChatWindow from '@/components/AIChatWindow.vue'

export default {
  name: 'App',
  components: {
    AIChatWindow
  },
  data() {
    return {
      // 用于判断是否为移动端视图的数据属性，默认为 false
      isMobile: false
    }
  },
  // 计算属性,用于从 vuex 中映射 siteInfo
  computed: {
    ...mapState('site', {
      // this.siteInfo 将映射到 $store.state.site.siteInfo
      siteInfo: 'siteInfo'
    }),
    // 判断是否应该显示聊天窗口
    shouldShowChat() {
      const currentPath = this.$route.path
      // 在 LoadingPage 和 PaymentPage 时不显示
      const excludedPaths = ['/nvivo/loading', '/nvivo/payment']
      return !excludedPaths.includes(currentPath)
    }
  },
  watch: {
    /**
     * @description 监听 isMobile 状态的变化，这是实现解决方案的核心。
     * @param {boolean} newIsMobile - isMobile 的新值。
     */
    isMobile(newIsMobile) {
      if (newIsMobile) {
        // 当切换到移动端视图 (isMobile 变为 true)
        // 1. 使用 this.$nextTick 等待 Vue 将 v-if 的元素渲染到 DOM 中
        this.$nextTick(() => {
          if (this.$refs.mobileTip) {
            // 2. 将提示栏元素从 #app 移动到 document.body 的末尾
            //    这是解决 transform 问题的关键步骤
            document.body.appendChild(this.$refs.mobileTip)
            // 3. 同时给 #app 容器添加 padding-bottom，防止内容被遮挡
            this.$el.style.paddingBottom = '70px'
          }
        })
      } else {
        // 当切换回 PC 视图 (isMobile 变为 false)
        // v-if 指令会自动从 DOM 中移除提示栏元素，我们无需手动处理。
        // 我们只需要移除为它预留的内边距。
        this.$el.style.paddingBottom = '0'
      }
    }
  },
  methods: {
    // ✨【新增】动态更新 favicon 的辅助方法
    /**
     * @description 动态更新网站的 favicon
     * @param {string} iconUrl - 新的 favicon URL
     */
    updateFavicon(iconUrl) {
      if (!iconUrl) return; // 如果 API 没有返回 URL，则不执行任何操作

      // 查找 head 中已存在的 icon link 标签
      let link = document.querySelector("link[rel*='icon']");

      if (link) {
        // 如果已存在 link 标签，则更新 href
        link.href = iconUrl;
      } else {
        // 如果不存在 (例如被插件移除或 index.html 模板中没有)，则创建新的 link 标签
        link = document.createElement('link');
        link.rel = 'icon';
        link.href = iconUrl;
        document.head.appendChild(link);
      }
    },

    // ✨【新增】动态更新 SEO meta 标签的辅助方法
    /**
     * @description 动态更新或创建 meta 标签
     * @param {string} name - meta 标签的 name 属性值
     * @param {string} content - meta 标签的 content 属性值
     */
    updateMetaTag(name, content) {
      if (!content) return; // 如果没有内容，则不执行任何操作

      // 查找 head 中已存在的 meta 标签
      let meta = document.querySelector(`meta[name="${name}"]`);

      if (meta) {
        // 如果已存在，则更新 content
        meta.setAttribute('content', content);
      } else {
        // 如果不存在，则创建新的 meta 标签
        meta = document.createElement('meta');
        meta.name = name;
        meta.content = content;
        document.head.appendChild(meta);
      }
    },

    // ✨【已修改】获取站点配置的逻辑
    loadSiteSettings() {
      // 1. 获取当前访问的域名
      const currentDomain = window.location.hostname
      
      console.log(`[App.vue] 正在为 ${currentDomain} 刷新站点配置...`)

      // 2. 派发 action (注意 'site/fetchSiteSettings' 包含命名空间)
      this.$store.dispatch('site/fetchSiteSettings', { domain: currentDomain })
        .then(data => {
          // 成功回调
          console.log('[App.vue] 站点配置加载成功。')
          
          // 3. ✨【已修改】根据 API 数据设置页面标题
          if(data.site_title) {
            document.title = data.site_title
          }

          // 4. ✨【新增】根据 API 数据设置 Favicon
          if (data.site_favicon_url) {
            this.updateFavicon(data.site_favicon_url)
          }

          // 5. ✨【新增】根据 API 数据设置 SEO 描述
          if (data.site_description) {
            this.updateMetaTag('description', data.site_description)
          }

          // 6. ✨【新增】根据 API 数据设置 SEO 关键字
          if (data.site_keywords) {
            this.updateMetaTag('keywords', data.site_keywords)
          }
        })
        .catch(error => {
          // 失败回调
          console.error('[App.vue] 加载站点配置失败:', error.message)
          // 可以在这里给用户一个全局提示
        })
    },

    /**
     * @description 检查当前窗口宽度，并更新 isMobile 的状态。
     */
    checkDevice() {
      this.isMobile = window.innerWidth < 768
    },

    /**
     * @description “复制链接”按钮的点击事件处理函数。
     */
    async copyLink() {
      try {
        await navigator.clipboard.writeText(window.location.href)
        alert('链接已成功复制到剪贴板！')
      } catch (err) {
        console.error('复制链接失败: ', err)
        alert('复制失败，您的浏览器可能不支持该功能，请手动复制。')
      }
    }
  },
  created() {
    // 调用方法获取站点配置 (每次刷新都会执行)
    this.loadSiteSettings()

    // (保留原有逻辑) 组件创建时，执行一次设备检查并添加监听器
    this.checkDevice()
    window.addEventListener('resize', this.checkDevice)
  },
  beforeDestroy() {
    // 组件销毁前，进行清理工作，确保万无一失
    // 1. 如果提示栏还在 body 中，手动移除它
    if (this.isMobile && this.$refs.mobileTip && this.$refs.mobileTip.parentNode === document.body) {
      document.body.removeChild(this.$refs.mobileTip);
    }
    // 2. 移除事件监听器，防止内存泄漏
    window.removeEventListener('resize', this.checkDevice)
  }
}
</script>

<style>
/* (样式保持原样) */
/* 这里的样式是全局的，会影响整个应用 */
body {
  margin: 0;
  background: linear-gradient(
    180deg,
    rgba(224, 237, 255, 1) 0%,
    rgba(255, 255, 255, 1) 100%
  );
}

/* 给 #app 容器添加过渡效果，使得底部内边距在出现和消失时有平滑的动画 */
#app {
  transition: padding-bottom 0.3s ease;
}

/* --- 移动端适配优化样式开始 --- */
.mobile-tip {
  /* 核心：使用 fixed 定位，使其固定在页面视口的底部，不受页面滚动影响 */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* 优化：使用 min-height，允许容器在内容换行时自适应增高，而不是写死高度 */
  min-height: 50px;
  height: auto; /* 配合 min-height 使用 */
  background-color: #eaf4ff;
  display: flex;
  justify-content: flex-start; /* 让文字和按钮两端对齐 */
  align-items: center; /* 垂直居中 */
  /* 优化：调整上下内边距，以便在文字换行时垂直方向有更多空间，更美观 */
  padding: 10px 16px;
  box-sizing: border-box;
  z-index: 9999; /* 设置一个非常高的层级，确保它能覆盖页面上所有其他内容 */
  border-top: 1px solid #dcdfe6; /* 顶部加一个细线，使其与页面内容有视觉分离 */
}

.mobile-tip .tip-text {
  font-size: 13px;
  color: #409eff;
  margin-right: 12px;
  /* 优化：允许文本在必要时换行，这是解决按钮在窄屏幕上被挤出屏幕的关键 */
  word-break: break-word;
  /* 优化：当空间不足时，文本容器会优先被压缩，而不是按钮 */
  flex-shrink: 1;
}

.mobile-tip .copy-button {
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 15px; /* 圆角让按钮更柔和 */
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  /* 优化：确保按钮本身及其文字不会被压缩变形或换行 */
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.2s; /* 添加一个背景色变化的过渡动画，提升交互体验 */
}

/* 按钮的悬停和点击效果 */
.mobile-tip .copy-button:hover {
  background-color: #66b1ff;
}

.mobile-tip .copy-button:active {
  background-color: #3a8ee6;
}
/* --- 移动端适配优化样式结束 --- */
</style>